import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaDownload,
  FaFileAlt,
  FaUser,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
} from "react-icons/fa";
import { reportsAPI, specialAssignmentsAPI } from "../../utils/api";
import { API_BASE_URL } from "../../config";
import "./Statistics.css";

// Helper: convert an image URL to a base64 data URL
const imageUrlToBase64 = async (url) => {
  try {
    const response = await fetch(url, { mode: "cors" });
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null; // Image could not be fetched; skip it
  }
};

const ReportDetails = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [specialAssignments, setSpecialAssignments] = useState([]);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError("");
        const { data } = await reportsAPI.getById(reportId);
        setReport(data);

        // Fetch special assignments submitted by this personnel on the report date
        if (data?.user?.id && data?.checkIn?.checkInTime) {
          const reportDate = new Date(data.checkIn.checkInTime).toISOString().split("T")[0];
          try {
            const saResponse = await specialAssignmentsAPI.getAll({ dateFrom: reportDate, dateTo: reportDate });
            const allAssignments = saResponse.data?.data || saResponse.data || [];
            const filtered = allAssignments.filter(
              (a) => String(a.personnelId || a.personnel?.id) === String(data.user.id)
            );
            setSpecialAssignments(filtered);
          } catch (saErr) {
            console.error("Failed to fetch special assignments:", saErr);
          }
        }
      } catch (err) {
        console.error("Failed to fetch report:", err);
        setError("Failed to load report details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (reportId) {
      fetchReport();
    }
  }, [reportId]);

  const handleExportPDF = async () => {
    setExportLoading(true);
    setError("");

    try {
      // --- Step 1: Collect and encode all check-out photos as base64 ---
      let imageBase64List = [];
      if (report.checkOutPhotos && report.checkOutPhotos.length > 0) {
        const encodingPromises = report.checkOutPhotos.map((url) => {
          const fullUrl = url.startsWith("http")
            ? url
            : `${API_BASE_URL}${url}`;
          return imageUrlToBase64(fullUrl);
        });
        const results = await Promise.all(encodingPromises);
        // Filter out any images that failed to load
        imageBase64List = results.filter(Boolean);
      }

      // --- Step 2: Try backend export first, passing images as base64 ---
      try {
        const response = await reportsAPI.exportReportPdf(reportId, {
          includeImages: true,
          images: imageBase64List, // array of base64 data URLs
        });

        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `report_${reportId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return; // Done — backend handled it
      } catch (backendError) {
        // If the backend doesn't yet support images, fall through to client-side generation
        console.warn(
          "Backend PDF export failed or does not support images, falling back to client-side:",
          backendError,
        );
      }

      // --- Step 3: Client-side PDF generation with jsPDF ---
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      const addText = (
        text,
        fontSize = 11,
        isBold = false,
        color = [30, 30, 30],
      ) => {
        doc.setFontSize(fontSize);
        doc.setFont("helvetica", isBold ? "bold" : "normal");
        doc.setTextColor(...color);
        const lines = doc.splitTextToSize(String(text ?? "N/A"), contentWidth);
        lines.forEach((line) => {
          if (y + fontSize + 4 > pageHeight - margin) {
            doc.addPage();
            y = margin;
          }
          doc.text(line, margin, y);
          y += fontSize + 4;
        });
      };

      const addSectionHeader = (title) => {
        y += 10;
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, y - 14, contentWidth, 20, "F");
        addText(title, 13, true, [50, 50, 50]);
        y += 4;
      };

      const addField = (label, value) => {
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text(`${label}: `, margin, y);
        const labelWidth = doc.getTextWidth(`${label}: `);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(30, 30, 30);
        const valueLines = doc.splitTextToSize(
          String(value ?? "N/A"),
          contentWidth - labelWidth,
        );
        doc.text(valueLines[0], margin + labelWidth, y);
        y += 16;
        // Extra lines if value wraps
        for (let i = 1; i < valueLines.length; i++) {
          doc.text(valueLines[i], margin, y);
          y += 16;
        }
      };

      // Title
      addText(`Report Details — ID: ${report.id}`, 18, true, [20, 20, 20]);
      y += 6;

      // Personnel
      addSectionHeader("Personnel Information");
      addField("Name", report.user?.name);
      addField("Position", report.user?.position);
      addField("Email", report.user?.email);

      // Site
      addSectionHeader("Site Information");
      addField("Site Name", report.checkIn?.site?.name);
      addField("Address", report.checkIn?.site?.address);
      addField("City", report.checkIn?.site?.city);
      addField("State", report.checkIn?.site?.state);

      // Check-in / Check-out
      addSectionHeader("Check-in / Check-out");
      addField(
        "Check-in Date",
        report.checkIn
          ? new Date(report.checkIn.checkInTime).toLocaleDateString()
          : null,
      );
      addField(
        "Check-in Time",
        report.checkIn
          ? new Date(report.checkIn.checkInTime).toLocaleTimeString()
          : null,
      );
      addField(
        "Check-out Date",
        report.checkIn?.checkOutTime
          ? new Date(report.checkIn.checkOutTime).toLocaleDateString()
          : "Not checked out",
      );
      addField(
        "Check-out Time",
        report.checkIn?.checkOutTime
          ? new Date(report.checkIn.checkOutTime).toLocaleTimeString()
          : "Not checked out",
      );

      // Report Content
      addSectionHeader("Report Content");
      if (report.content) {
        addText(report.content, 11, false);
      } else {
        addText("No report content available.", 11, false, [120, 120, 120]);
      }

      // Metadata
      addSectionHeader("Report Metadata");
      addField("Created", new Date(report.createdAt).toLocaleString());
      addField("Last Updated", new Date(report.updatedAt).toLocaleString());
      addField("Status", "Completed");

      // --- Step 4: Embed check-out photos ---
      if (imageBase64List.length > 0) {
        doc.addPage();
        y = margin;
        addText("Check-out Photos", 16, true, [20, 20, 20]);
        y += 8;

        const imgMaxWidth = (contentWidth - 20) / 2; // two columns
        const imgMaxHeight = 180;
        let col = 0;

        for (let i = 0; i < imageBase64List.length; i++) {
          const base64 = imageBase64List[i];

          // Detect format from data URL header
          const format = base64.startsWith("data:image/png") ? "PNG" : "JPEG";

          // Calculate dimensions to fit within max bounds while preserving aspect ratio
          const img = await new Promise((resolve) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.src = base64;
          });
          const ratio = Math.min(
            imgMaxWidth / img.width,
            imgMaxHeight / img.height,
          );
          const drawWidth = img.width * ratio;
          const drawHeight = img.height * ratio;

          const xPos = margin + col * (imgMaxWidth + 20);

          if (y + drawHeight > pageHeight - margin) {
            doc.addPage();
            y = margin;
            col = 0;
          }

          try {
            doc.addImage(base64, format, xPos, y, drawWidth, drawHeight);
          } catch {
            addText(
              `[Image ${i + 1} could not be embedded]`,
              10,
              false,
              [150, 150, 150],
            );
          }

          // Label under image
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(100, 100, 100);
          doc.text(`Photo ${i + 1}`, xPos, y + drawHeight + 12);

          col++;
          if (col >= 2) {
            col = 0;
            y += drawHeight + 30;
          }
        }
      }

      // --- Step 5: Save ---
      doc.save(`report_${reportId}.pdf`);
    } catch (err) {
      console.error("Failed to export report PDF:", err);
      setError("Failed to export report PDF. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate("/admin/statistics")}>
          <FaArrowLeft className="me-2" /> Back to Statistics
        </Button>
      </Container>
    );
  }

  if (!report) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">Report not found.</Alert>
        <Button variant="primary" onClick={() => navigate("/admin/statistics")}>
          <FaArrowLeft className="me-2" /> Back to Statistics
        </Button>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>Report Details</h2>
              <p className="text-muted">Report ID: {report.id}</p>
            </div>
            <div>
              <Button
                variant="outline-primary"
                className="me-2"
                onClick={() => navigate("/admin/statistics")}
              >
                <FaArrowLeft className="me-2" /> Back to Statistics
              </Button>
              <Button
                variant="success"
                onClick={handleExportPDF}
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Exporting...
                  </>
                ) : (
                  <>
                    <FaDownload className="me-2" /> Export
                  </>
                )}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Report Information */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FaUser className="me-2" /> Personnel Information
              </h5>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>Name:</strong>{" "}
                {report.user ? report.user.name : "Unknown"}
              </p>
              <p>
                <strong>Position:</strong>{" "}
                {report.user ? report.user.position || "N/A" : "N/A"}
              </p>
              <p>
                <strong>Email:</strong>{" "}
                {report.user ? report.user.email || "N/A" : "N/A"}
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FaMapMarkerAlt className="me-2" /> Site Information
              </h5>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>Site Name:</strong>{" "}
                {report.checkIn?.site?.name ?? "Unknown"}
              </p>
              <p>
                <strong>Address:</strong>{" "}
                {report.checkIn?.site?.address ?? "N/A"}
              </p>
              <p>
                <strong>City:</strong> {report.checkIn?.site?.city ?? "N/A"}
              </p>
              <p>
                <strong>State:</strong> {report.checkIn?.site?.state ?? "N/A"}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Check-in/Check-out Information */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FaCalendarAlt className="me-2" /> Check-in Information
              </h5>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>Check-in Date:</strong>{" "}
                {report.checkIn
                  ? new Date(report.checkIn.checkInTime).toLocaleDateString()
                  : "N/A"}
              </p>
              <p>
                <strong>Check-in Time:</strong>{" "}
                {report.checkIn
                  ? new Date(report.checkIn.checkInTime).toLocaleTimeString()
                  : "N/A"}
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FaClock className="me-2" /> Check-out Information
              </h5>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>Check-out Date:</strong>{" "}
                {report.checkIn?.checkOutTime
                  ? new Date(report.checkIn.checkOutTime).toLocaleDateString()
                  : "Not checked out"}
              </p>
              <p>
                <strong>Check-out Time:</strong>{" "}
                {report.checkIn?.checkOutTime
                  ? new Date(report.checkIn.checkOutTime).toLocaleTimeString()
                  : "Not checked out"}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Report Content */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FaFileAlt className="me-2" /> Report Content
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="p-3 border rounded bg-light">
                {report.content ? (
                  <p style={{ whiteSpace: "pre-wrap" }}>{report.content}</p>
                ) : (
                  <p className="text-muted">No report content available.</p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Check-out Photos */}
      {report.checkOutPhotos && report.checkOutPhotos.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Check-out Photos</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  {report.checkOutPhotos.map((url, index) => (
                    <Col md={4} key={index} className="mb-3">
                      <img
                        src={`${API_BASE_URL}${url}`}
                        alt={`Check-out ${index + 1}`}
                        className="img-fluid rounded border"
                        onError={(e) => {
                          e.target.src =
                            "https://placehold.co/400x300?text=Image+Not+Found";
                        }}
                      />
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="mt-2"
                        onClick={() =>
                          window.open(`${API_BASE_URL}${url}`, "_blank")
                        }
                      >
                        <FaFileAlt className="me-2" /> Open Full Image
                      </Button>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Special Assignments submitted on the report date */}
      {specialAssignments.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Special Assignments</h5>
              </Card.Header>
              <Card.Body>
                {specialAssignments.map((assignment) => {
                  const personnelName =
                    assignment.personnel?.name ||
                    `${assignment.personnel?.firstName || ""} ${assignment.personnel?.lastName || ""}`.trim() ||
                    "N/A";
                  const personnelEmail = assignment.personnel?.email || "N/A";
                  const submittedOn = assignment.createdAt || assignment.created_at;
                  return (
                    <Card key={assignment.id} className="mb-3 border">
                      <Card.Body>
                        {assignment.title && (
                          <h6 className="fw-bold mb-3">{assignment.title}</h6>
                        )}
                        <Row>
                          <Col md={6}>
                            <p className="mb-1"><strong>Assigned Personnel:</strong> {personnelName}</p>
                            <p className="mb-1"><strong>Email:</strong> {personnelEmail}</p>
                            <p className="mb-1"><strong>Description:</strong> {assignment.description || "N/A"}</p>
                          </Col>
                          <Col md={6}>
                            <p className="mb-1"><strong>Site Name:</strong> {assignment.siteName || "N/A"}</p>
                            <p className="mb-1"><strong>Location:</strong> {assignment.location || "N/A"}</p>
                            {assignment.latitude && assignment.longitude && (
                              <p className="mb-1">
                                <strong>GPS Coordinates:</strong>{" "}
                                {parseFloat(assignment.latitude).toFixed(6)}, {parseFloat(assignment.longitude).toFixed(6)}
                              </p>
                            )}
                            <p className="mb-1">
                              <strong>Submitted On:</strong>{" "}
                              {submittedOn ? new Date(submittedOn).toLocaleString() : "N/A"}
                            </p>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  );
                })}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Document Attachments */}
      {report.documents && report.documents.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FaFileAlt className="me-2" />
                  Document Attachments ({report.documents.length})
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="list-group">
                  {report.documents.map((doc, index) => {
                    const docPath =
                      typeof doc === "string" ? doc : doc.path || doc.url;
                    const docName =
                      typeof doc === "object"
                        ? doc.name || docPath?.split("/").pop()
                        : docPath?.split("/").pop();
                    const docSize =
                      typeof doc === "object" && doc.size
                        ? `${(doc.size / 1024).toFixed(2)} KB`
                        : null;
                    const docUrl = docPath?.startsWith("http")
                      ? docPath
                      : docPath?.startsWith("/")
                        ? `${API_BASE_URL}${docPath}`
                        : `${API_BASE_URL}/${docPath}`;

                    return (
                      <div
                        key={index}
                        className="list-group-item d-flex justify-content-between align-items-center mb-2"
                      >
                        <div>
                          <FaFileAlt className="me-2" />
                          <strong>{docName || `Document ${index + 1}`}</strong>
                          {docSize && (
                            <small className="text-muted ms-2">
                              ({docSize})
                            </small>
                          )}
                        </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => window.open(docUrl, "_blank")}
                        >
                          <FaDownload className="me-1" />
                          Download
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Report Metadata */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Report Metadata</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>Report Created:</strong>{" "}
                    {new Date(report.createdAt).toLocaleString()}
                  </p>
                  <p>
                    <strong>Last Updated:</strong>{" "}
                    {new Date(report.updatedAt).toLocaleString()}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Status:</strong>{" "}
                    <Badge bg="success">Completed</Badge>
                  </p>
                  <p>
                    <strong>Report ID:</strong> {report.id}
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ReportDetails;
